import { useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  Typography,
  Spin,
  Alert,
  Space,
  Switch,
  InputNumber,
  Input,
} from "antd";
import {
  ArrowLeftOutlined,
  ApiOutlined,
  SettingOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useDevice, useUpdateDevice } from "../hooks/useDevices";
import {
  useDeviceProfile,
  useDeviceProfiles,
  useDeviceSchema,
  useDeviceProperties,
  useUpdateDeviceProperties,
  useDevicePropertiesHistory,
} from "../hooks/useDeviceProfiles";
import { HeartbeatBadge } from "../components/HeartbeatBadge";
import { useQuery } from "@tanstack/react-query";
import { interactApi } from "../api/interact";
import { useMqttClient } from "../hooks/useMqttClient";
import { MqttMessageLog } from "../components/MqttMessageLog";
import { getMqttBrokerUrl } from "../utils/mqttUrl";
import type { ServiceProperty } from "../api/deviceProfiles";

const { Title, Text } = Typography;

/** 根据 property 定义渲染编辑控件 */
function PropertyControl({
  def,
  value,
  onChange,
  disabled,
}: {
  def: ServiceProperty;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled: boolean;
}) {
  switch (def.type) {
    case "enum":
      return (
        <Select
          value={value as string}
          onChange={onChange}
          disabled={disabled}
          style={{ width: "100%" }}
          options={def.values?.map((v) => ({ value: v, label: v }))}
        />
      );
    case "boolean":
      return (
        <Switch
          checked={value as boolean}
          onChange={(v) => onChange(v)}
          disabled={disabled}
        />
      );
    case "integer":
      return (
        <InputNumber
          value={value as number}
          onChange={(v) => onChange(v)}
          disabled={disabled}
          min={def.min}
          max={def.max}
          style={{ width: "100%" }}
          addonAfter={def.unit}
        />
      );
    case "float":
      return (
        <InputNumber
          value={value as number}
          onChange={(v) => onChange(v)}
          disabled={disabled}
          min={def.min}
          max={def.max}
          step={0.1}
          style={{ width: "100%" }}
          addonAfter={def.unit}
        />
      );
    default:
      return (
        <Input
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );
  }
}

export default function DeviceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: device, isLoading, error } = useDevice(id!);
  const [historyPage, setHistoryPage] = useState(1);

  // 设备自身的属性定义（创建时从 Profile 复制，独立存在）
  const { data: schema } = useDeviceSchema(id);
  // Profile 仅用于展示型号标签
  const { data: profile } = useDeviceProfile(device?.profile_id || undefined);
  // 所有型号列表（用于重新绑定）
  const { data: allProfiles } = useDeviceProfiles();

  // Properties
  const { data: propertiesData } = useDeviceProperties(id);
  const updateMutation = useUpdateDeviceProperties(id);
  const updateDeviceMutation = useUpdateDevice();
  const { data: historyData } = useDevicePropertiesHistory(id);

  // 重新绑定型号弹窗
  const [rebindOpen, setRebindOpen] = useState(false);
  const [rebindProfileId, setRebindProfileId] = useState<string | undefined>();

  const historyQuery = useQuery({
    queryKey: ["history", id, historyPage],
    queryFn: () => interactApi.history(id!, { page: historyPage }),
    enabled: !!id,
  });

  // MQTT 实时连接，订阅设备属性变更
  const handleCommandReceived = useCallback(
    (props: Record<string, unknown>) => {
      setEditBuffer((prev) => ({ ...prev, ...props }));
    },
    [],
  );

  const handleReportReceived = useCallback((props: Record<string, unknown>) => {
    setEditBuffer((prev) => ({ ...prev, ...props }));
  }, []);

  const {
    status: mqttStatus,
    messages: mqttMessages,
    connect: mqttConnect,
    disconnect: mqttDisconnect,
    clearMessages: clearMqttMessages,
  } = useMqttClient({
    deviceId: id!,
    onCommandReceived: handleCommandReceived,
    onReportReceived: handleReportReceived,
  });

  // 临时编辑状态
  const [editBuffer, setEditBuffer] = useState<Record<string, unknown>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useMemo(() => {
    if (propertiesData) {
      setEditBuffer({ ...propertiesData.properties });
      setHasChanges(false);
    }
  }, [propertiesData]);

  const handlePropertyChange = (name: string, value: unknown) => {
    setEditBuffer((prev) => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleSaveProperties = () => {
    updateMutation.mutate(
      { properties: editBuffer },
      {
        onSuccess: () => setHasChanges(false),
      },
    );
  };

  if (isLoading)
    return <Spin size="large" style={{ display: "block", marginTop: 80 }} />;
  if (error)
    return (
      <Alert type="error" message={error.message} style={{ marginTop: 40 }} />
    );
  if (!device) return null;

  const historyColumns = [
    {
      title: "输入类型",
      dataIndex: "input_type",
      width: 90,
      render: (t: string) => (
        <Tag color={t === "voice" ? "blue" : "green"}>
          {t === "voice" ? "语音" : "文本"}
        </Tag>
      ),
    },
    { title: "输入内容", dataIndex: "text_input", ellipsis: true },
    { title: "AI 回复", dataIndex: "response_text", ellipsis: true },
    {
      title: "后端",
      dataIndex: "backend_used",
      width: 90,
      render: (b: string) =>
        b ? (
          <Tag color={b === "local" ? "green" : "blue"}>
            {b === "local" ? "本地" : "远程"}
          </Tag>
        ) : (
          "—"
        ),
    },
    {
      title: "时间",
      dataIndex: "created_at",
      width: 160,
      render: (t: string) => new Date(t).toLocaleString("zh-CN"),
    },
  ];

  const propertyHistoryColumns = [
    {
      title: "属性",
      dataIndex: "property_name",
      key: "property_name",
      width: 100,
    },
    {
      title: "旧值",
      dataIndex: "old_value",
      key: "old_value",
      width: 100,
      render: (v: unknown) => v ?? "—",
    },
    { title: "新值", dataIndex: "new_value", key: "new_value", width: 100 },
    {
      title: "来源",
      dataIndex: "source",
      key: "source",
      width: 80,
      render: (s: string) => {
        const colors: Record<string, string> = {
          user: "blue",
          device: "green",
          system: "orange",
        };
        const labels: Record<string, string> = {
          user: "用户",
          device: "设备",
          system: "系统",
        };
        return <Tag color={colors[s] || "default"}>{labels[s] || s}</Tag>;
      },
    },
    {
      title: "时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (t: string) => new Date(t).toLocaleString("zh-CN"),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/devices")}
        >
          返回列表
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {device.name}
        </Title>
        <HeartbeatBadge status={device.status} lastSeen={device.last_seen} />
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="设备信息">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="设备 ID">
                <code style={{ fontSize: 12 }}>{device.id}</code>
              </Descriptions.Item>
              <Descriptions.Item label="设备名称">
                {device.name}
              </Descriptions.Item>
              <Descriptions.Item label="设备型号">
                <Space>
                  {profile ? (
                    <Tag color="blue">{profile.display_name}</Tag>
                  ) : device.profile_id ? (
                    <Tag>{device.profile_id}</Tag>
                  ) : (
                    <Text type="secondary">未设置</Text>
                  )}
                  <Button
                    size="small"
                    icon={<SwapOutlined />}
                    onClick={() => {
                      setRebindProfileId(device.profile_id || undefined);
                      setRebindOpen(true);
                    }}
                  >
                    更换型号
                  </Button>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="描述">
                {device.description || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="当前状态">
                <HeartbeatBadge
                  status={device.status}
                  lastSeen={device.last_seen}
                />
              </Descriptions.Item>
              <Descriptions.Item label="最后活跃">
                {device.last_seen
                  ? new Date(device.last_seen).toLocaleString("zh-CN")
                  : "从未连接"}
              </Descriptions.Item>
              <Descriptions.Item label="注册时间">
                {new Date(device.created_at).toLocaleString("zh-CN")}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <>
                <ApiOutlined /> MQTT 实时连接
              </>
            }
            extra={
              <Space>
                <Tag
                  color={
                    mqttStatus === "connected"
                      ? "green"
                      : mqttStatus === "connecting"
                        ? "orange"
                        : "red"
                  }
                >
                  {mqttStatus === "connected"
                    ? "已连接"
                    : mqttStatus === "connecting"
                      ? "连接中"
                      : "未连接"}
                </Tag>
                {mqttStatus === "connected" ? (
                  <Button size="small" onClick={mqttDisconnect}>
                    断开
                  </Button>
                ) : (
                  <Button size="small" onClick={mqttConnect}>
                    连接
                  </Button>
                )}
              </Space>
            }
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Broker: {getMqttBrokerUrl()}
            </Text>
          </Card>
          {/* MQTT 实时消息日志 */}
          <div style={{ marginTop: 12 }}>
            <MqttMessageLog
              messages={mqttMessages}
              onClear={clearMqttMessages}
              height={180}
            />
          </div>
        </Col>
      </Row>

      {/* 设备属性 */}
      <Card
        title={
          <>
            <SettingOutlined /> 设备属性
          </>
        }
        style={{ marginTop: 16 }}
        extra={
          hasChanges && (
            <Button
              type="primary"
              size="small"
              onClick={handleSaveProperties}
              loading={updateMutation.isPending}
            >
              保存属性
            </Button>
          )
        }
      >
        {(() => {
          /* 以设备自身的属性定义（schema）驱动渲染，完全不依赖 Profile */
          const props = propertiesData?.properties;
          if (!props || Object.keys(props).length === 0) {
            return (
              <Text type="secondary">
                {schema && schema.length > 0
                  ? "正在加载属性值..."
                  : "该设备无属性定义"}
              </Text>
            );
          }

          /* 从设备自身的 schema 构建属性定义查找表 */
          const defMap: Record<string, ServiceProperty> = {};
          if (schema) {
            for (const svc of schema) {
              for (const p of svc.properties) {
                defMap[p.name] = p;
              }
            }
          }

          return (
            <Row gutter={[16, 12]}>
              {Object.entries(props).map(([name, val]) => {
                const def = defMap[name];
                return (
                  <Col xs={24} sm={12} md={8} key={name}>
                    <div style={{ marginBottom: 4 }}>
                      <Text type="secondary">
                        {def ? def.display_name : name}
                      </Text>
                      {def?.unit && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {" "}
                          ({def.unit})
                        </Text>
                      )}
                    </div>
                    {def ? (
                      <PropertyControl
                        def={def}
                        value={editBuffer[name] ?? def.default}
                        onChange={(v) => handlePropertyChange(name, v)}
                        disabled={updateMutation.isPending}
                      />
                    ) : (
                      <Input
                        value={String(val)}
                        disabled
                        style={{ width: "100%" }}
                      />
                    )}
                  </Col>
                );
              })}
            </Row>
          );
        })()}
      </Card>

      {/* 属性变更历史 */}
      {historyData && historyData.logs.length > 0 && (
        <Card title="属性变更历史" style={{ marginTop: 16 }} size="small">
          <Table
            dataSource={historyData.logs}
            columns={propertyHistoryColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        </Card>
      )}

      {/* 历史交互记录 */}
      <Card title="历史交互记录" style={{ marginTop: 16 }}>
        <Table
          dataSource={historyQuery.data?.items}
          columns={historyColumns}
          rowKey="id"
          loading={historyQuery.isLoading}
          size="small"
          pagination={{
            total: historyQuery.data?.total,
            pageSize: 20,
            current: historyPage,
            onChange: setHistoryPage,
            showTotal: (t) => `共 ${t} 条`,
          }}
          locale={{ emptyText: "暂无交互记录" }}
        />
      </Card>

      {/* 重新绑定型号弹窗 */}
      <Modal
        title="更换设备型号"
        open={rebindOpen}
        onCancel={() => setRebindOpen(false)}
        onOk={() => {
          if (rebindProfileId !== undefined) {
            updateDeviceMutation.mutate(
              { id: id!, data: { profile_id: rebindProfileId } },
              { onSuccess: () => setRebindOpen(false) },
            );
          }
        }}
        confirmLoading={updateDeviceMutation.isPending}
        okText="确认更换"
        cancelText="取消"
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          更换型号将用新型号的属性定义和默认值替换当前设备的属性，旧属性数据会丢失。请确认新型号与设备兼容。
        </Text>
        <Select
          value={rebindProfileId}
          onChange={setRebindProfileId}
          style={{ width: "100%" }}
          placeholder="选择新型号"
          allowClear
          options={allProfiles?.map((p) => ({
            value: p.id,
            label: `${p.display_name} (${p.category === "sensor" ? "传感器" : "执行器"})`,
          }))}
        />
      </Modal>
    </div>
  );
}
